import { yup } from '@medely/ui-kit';
import moment from 'moment-timezone';
import { REASON_OTHER_VALUE } from './constants';

interface ShiftTimeContext {
  startsTime?: string;
  endsTime?: string;
  timezone: string;
}

function buildSelectedMoment(dateValue: string, timeValue: string, timezone: string) {
  const timeParsed = moment.tz(timeValue, timezone);
  return moment
    .tz(dateValue, 'YYYY-MM-DD', timezone)
    .hour(timeParsed.hour())
    .minute(timeParsed.minute())
    .second(0)
    .millisecond(0);
}

function isNotEarlierThanTwoHoursBefore(
  dateValue: string,
  timeValue: string,
  scheduledTime: string,
  timezone: string,
): boolean {
  const earliest = moment.tz(scheduledTime, timezone).subtract(2, 'hours');
  const selected = buildSelectedMoment(dateValue, timeValue, timezone);
  return selected.isSameOrAfter(earliest);
}

function isNotLaterThanTwoHoursAfter(
  dateValue: string,
  timeValue: string,
  scheduledTime: string,
  timezone: string,
): boolean {
  const latest = moment.tz(scheduledTime, timezone).add(2, 'hours');
  const selected = buildSelectedMoment(dateValue, timeValue, timezone);
  return selected.isSameOrBefore(latest);
}

export const createEditClockTimesSchema = ({ startsTime, endsTime, timezone }: ShiftTimeContext) =>
  yup.object().shape({
    approved_clock_in_date: yup.string().required('Required'),
    approved_clock_in_time: yup
      .string()
      .required('Required')
      .test(
        'not-earlier-than-2h-before-start',
        'Cannot be earlier than 2 hours before scheduled start time',
        function (value) {
          if (!value || !startsTime) {
            return true;
          }
          const date = this.parent.approved_clock_in_date;
          if (!date) {
            return true;
          }
          return isNotEarlierThanTwoHoursBefore(date, value, startsTime, timezone);
        },
      ),
    approved_clock_out_date: yup.string().required('Required'),
    approved_clock_out_time: yup
      .string()
      .required('Required')
      .test(
        'not-later-than-2h-after-end',
        'Cannot be later than 2 hours after scheduled end time',
        function (value) {
          if (!value || !endsTime) {
            return true;
          }
          const date = this.parent.approved_clock_out_date;
          if (!date) {
            return true;
          }
          return isNotLaterThanTwoHoursAfter(date, value, endsTime, timezone);
        },
      ),
    reason: yup.string().required('Please select a reason'),
    reason_other: yup.string().when('reason', {
      is: REASON_OTHER_VALUE,
      then: (schema) => schema.required('Please provide a note when reason is Other'),
      otherwise: (schema) => schema.optional(),
    }),
  });

export const editClockTimesSchema = createEditClockTimesSchema({ timezone: moment.tz.guess() });

export type EditClockTimesFormValues = yup.InferType<typeof editClockTimesSchema>;
